import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, ImageOff, Heart, Share2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useGalleryImage } from "./useGalleryImage";
import { shareGalleryItem } from "./GalleryCard";

/** The lightbox's own image — same authenticated blob-fetch path as GalleryCard's BtsImage
 * (see useGalleryImage.js), a neutral placeholder while loading, a quiet fallback on error.
 * Natural proportions, bounded by max-height (90vh) and max-width (see wrapper classes on
 * GalleryLightbox below) — never cropped, never upscaled past its own real size. */
function LightboxImage({ item }) {
  const { objectUrl, status } = useGalleryImage(item.image_url);
  if (status === "error") {
    return (
      <div className="flex h-[40vh] w-[70vw] max-w-sm items-center justify-center rounded-md bg-ink-deep/40" data-testid="gallery-lightbox-image-fallback">
        <ImageOff className="h-8 w-8 text-white/40" />
      </div>
    );
  }
  if (!objectUrl) {
    return <div className="h-[40vh] w-[70vw] max-w-sm animate-pulse rounded-md bg-ink-deep/40" data-testid="gallery-lightbox-image-loading" />;
  }
  return (
    <img
      src={objectUrl}
      alt=""
      data-testid="gallery-lightbox-image"
      className="block max-h-[90vh] max-w-full rounded-md object-contain"
    />
  );
}

/** Video window — a YouTube embed that autoplays, related-off, minimal branding. Unmounts
 * entirely (never just hidden) the instant the lightbox closes — that IS how playback/audio
 * stops, there is no separate pause call needed. Fixed 16:9, sized by the wrapper. */
function LightboxVideo({ item }) {
  return (
    <div className="relative w-full overflow-hidden rounded-md bg-black" style={{ aspectRatio: "16 / 9" }} data-testid="gallery-lightbox-video-frame">
      <iframe
        title={`gallery-lightbox-video-${item.id}`}
        data-testid="gallery-lightbox-video-iframe"
        className="absolute inset-0 h-full w-full"
        src={`https://www.youtube-nocookie.com/embed/${item.youtube_video_id}?autoplay=1&rel=0&modestbranding=1`}
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

/** The Gallery (2026-11, follow-up) — ONE lightbox for both video and BTS photo, replacing the
 * old split (video played in-place at thumbnail size; only photos opened this modal). Centred
 * window, not full-screen: ~80vw up to ~960px wide for video (16:9); a photo fits within ~90vh
 * at its own natural proportions. On mobile, nearly full width with a small margin either way.
 * Closing (× button, Esc, or backdrop click) unmounts the whole window — for video, that IS
 * what stops playback/audio, there is no separate pause step. No prev/next arrows or tag chips
 * here (G3) — the like toggle and Share action (Stage 2+3, 2026-11) were added below, mirroring
 * the card's own buttons exactly; `item` is always the SAME object Gallery.jsx looks up live
 * from its `items`/`fallbackItem` state (never a disconnected snapshot), so a like toggled
 * from the card or from here shows up in both places immediately with no refetch. */
export function GalleryLightbox({ item, onClose, onToggleLike, likePending }) {
  const containerRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  useEffect(() => {
    if (!item) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    // Scroll lock without resetting scroll position — toggling `overflow` never changes
    // `scrollTop`, so the page behind is exactly where it was once this unmounts.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    previouslyFocusedRef.current = document.activeElement;
    containerRef.current?.focus();
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      previouslyFocusedRef.current?.focus?.();
    };
  }, [item, onClose]);

  if (!item) return null;
  const creator = item.creator;
  const initials = (creator?.name || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  const sub = creator ? [creator.role, creator.city].filter(Boolean).join(" · ") : "";
  const isVideo = item.type === "video";

  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-ink/90 p-4 sm:p-8"
      data-testid="gallery-lightbox-backdrop"
      onClick={onClose}
    >
      <div
        ref={containerRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        data-testid="gallery-lightbox-window"
        className={`relative flex max-h-full flex-col items-center gap-4 outline-none ${isVideo ? "w-[calc(100vw-32px)] sm:w-[80vw] sm:max-w-[960px]" : "max-w-[calc(100vw-32px)] sm:max-w-[80vw]"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" onClick={onClose} aria-label="Close" data-testid="gallery-lightbox-close"
          className="absolute -top-10 right-0 sm:-top-2 sm:-right-2 z-10 h-11 w-11 rounded-full border border-white/25 text-white flex items-center justify-center hover:bg-white/10 transition-colors">
          <X className="h-4 w-4" />
        </button>

        {isVideo ? <LightboxVideo item={item} /> : <LightboxImage item={item} />}

        <div className="flex w-full max-w-[460px] items-center gap-3">
          {creator ? (
            <Link to={`/member/${creator.freelancer_id}`} data-testid="gallery-lightbox-creator-avatar"
              className="h-8 w-8 shrink-0 rounded-full bg-brand text-ink font-bold text-[11px] flex items-center justify-center">
              {initials}
            </Link>
          ) : (
            <span data-testid="gallery-lightbox-creator-avatar" className="h-8 w-8 shrink-0 rounded-full bg-brand text-ink font-bold text-[11px] flex items-center justify-center">
              {initials}
            </span>
          )}
          <div className="min-w-0 flex-1">
            {creator ? (
              <Link to={`/member/${creator.freelancer_id}`} data-testid="gallery-lightbox-creator-name"
                className="text-[14px] font-semibold text-white border-b border-white/35 hover:border-white/60 transition-colors">
                {creator.name}
              </Link>
            ) : (
              <span data-testid="gallery-lightbox-creator-name" className="text-[14px] font-semibold text-white">
                NJM Build member
              </span>
            )}
            {sub && <div className="mt-0.5 text-[12px] text-white/60 truncate">{sub}</div>}
          </div>
          <button type="button" onClick={() => shareGalleryItem(item.id)} aria-label="Share"
            data-testid="gallery-lightbox-share-button"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-colors">
            <Share2 className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => onToggleLike(item)} disabled={likePending} aria-label={item.liked_by_me ? "Unlike" : "Like"}
            data-testid="gallery-lightbox-like-button"
            className={`flex shrink-0 items-center gap-1.5 text-[13px] font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${item.liked_by_me ? "text-red-500" : "text-white/60 hover:text-white"}`}>
            <Heart className="h-4 w-4" fill={item.liked_by_me ? "currentColor" : "none"} />
            <span data-testid="gallery-lightbox-like-count">{item.like_count || 0}</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

